/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useContext, useEffect } from 'react';
import './styles.css';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { UserContext } from '../../../providers/UserProvider';

const SalesCenterManualFirebaseSettings = ({ salesCenter, onCompletedManualFirebaseSteps }) => {
  const userContext = useContext(UserContext);

  const automatedSteps = [
    'Create Firestore Database',
    'Create Real Time Database',
    'Add Auth Providers',
    'Add Auth Domain',
  ];

  const manualSteps = [
    [
      'Enable Storage',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/storage`,
      '/images/setup/firebaseStorage.png',
    ],
    [
      'Upgrade To Blaze Plan',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/overview`,
      '/images/setup/firebaseUpgrade.png',
    ],
    [
      'Enable Google Analytics',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/settings/integrations`,
      '/images/setup/firebaseAnalytics.png',
    ],
  ];

  const existingStatus = salesCenter.firebaseServicesSetup || {};
  const alreadyComplete = existingStatus.status === 'COMPLETE';

  const [automationStatus, setAutomationStatus] = useState(
    alreadyComplete ? 'COMPLETE' : 'IDLE',
  );
  const [automationMessage, setAutomationMessage] = useState(
    alreadyComplete ? 'All automated steps complete' : '',
  );
  const [automationError, setAutomationError] = useState(
    existingStatus.error ? existingStatus.message : '',
  );
  const [currentManualStep, setCurrentManualStep] = useState(0);

  useEffect(() => {
    if (alreadyComplete) return;

    setAutomationStatus('IN-PROGRESS');
    setAutomationMessage('Starting automated Firebase setup...');

    userContext.socket.emit(
      'setupFirebaseServices',
      salesCenter.subdomain,
      (status, message) => {
        if (status === 'COMPLETE') {
          setAutomationStatus('COMPLETE');
          setAutomationMessage('All automated steps complete');
          setAutomationError('');
        } else if (status === 'ERROR') {
          setAutomationStatus('ERROR');
          setAutomationError(message || 'Unknown error');
        } else if (status === 'IN-PROGRESS') {
          setAutomationStatus('IN-PROGRESS');
          setAutomationMessage(message || 'Setting up Firebase services...');
        }
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerManualNextStep = () => {
    if (currentManualStep + 1 === manualSteps.length) {
      onCompletedManualFirebaseSteps();
      return;
    }
    setCurrentManualStep((prev) => prev + 1);
  };

  const retryAutomation = () => {
    setAutomationStatus('IN-PROGRESS');
    setAutomationMessage('Retrying automated Firebase setup...');
    setAutomationError('');

    userContext.socket.emit(
      'setupFirebaseServices',
      salesCenter.subdomain,
      (status, message) => {
        if (status === 'COMPLETE') {
          setAutomationStatus('COMPLETE');
          setAutomationMessage('All automated steps complete');
          setAutomationError('');
        } else if (status === 'ERROR') {
          setAutomationStatus('ERROR');
          setAutomationError(message || 'Unknown error');
        }
      },
    );
  };

  const automationComplete = automationStatus === 'COMPLETE';
  const activeManualStep = manualSteps[currentManualStep];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      <div
        style={{
          padding: '20px', border: '1px solid #ddd', borderRadius: '8px', margin: '0 20px',
        }}
      >
        <h4 style={{ marginTop: 0 }}>Automated Setup</h4>
        {
          automatedSteps.map((step, index) => (
            <div
              key={step}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: '1px dotted #ccc',
                gap: '10px',
              }}
            >
              <div style={{ width: '30px', fontWeight: 'bold' }}>{index + 1}</div>
              <div style={{ flexGrow: 1 }}>{step}</div>
              <div>
                {automationComplete && <CheckCircleIcon style={{ color: 'green' }} />}
                {automationStatus === 'IN-PROGRESS' && <CircularProgress size={20} />}
                {automationStatus === 'ERROR' && <ErrorIcon style={{ color: 'red' }} />}
              </div>
            </div>
          ))
        }
        {
          automationStatus === 'IN-PROGRESS' && (
            <div style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
              {automationMessage}
            </div>
          )
        }
        {
          automationStatus === 'ERROR' && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>
                Error: {automationError}
              </div>
              <Button variant="outlined" color="primary" onClick={retryAutomation}>
                Retry
              </Button>
            </div>
          )
        }
      </div>

      {
        automationComplete && (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
              style={{
                display: 'flex', flexDirection: 'column', width: '400px', margin: 'auto', paddingLeft: '40px',
              }}
            >
              <h4 style={{ marginTop: 0 }}>Manual Steps</h4>
              {
                manualSteps.map((step, index) => (
                  <div
                    key={step[0]}
                    style={{
                      width: '330px', height: '40px', borderBottom: '1px dotted black', display: 'flex',
                    }}
                  >
                    <div
                      style={{
                        width: '30px', fontWeight: 'bold', margin: 'auto', marginLeft: '0px', marginRight: '0px',
                      }}
                    >
                      {index + 5}
                    </div>
                    <div
                      style={{
                        width: '220px', margin: 'auto', marginLeft: '0px', marginRight: '0px',
                      }}
                    >
                      <a href={step[1]} target="_blank" rel="noreferrer"> {step[0]} </a>
                    </div>
                    <div>
                      {
                        (index === currentManualStep) && (
                          <Button variant="contained" color="primary" onClick={triggerManualNextStep}>
                            DONE
                          </Button>
                        )
                      }
                    </div>
                  </div>
                ))
              }
            </div>
            <div
              style={{
                margin: '30px',
                border: '3px solid red',
                borderRadius: '20px',
                padding: '10px',
                textAlign: 'center',
                flexGrow: '1',
                maxWidth: '700px',
              }}
            >
              <img
                src={process.env.PUBLIC_URL + activeManualStep[2]}
                alt="setup step"
                style={{ height: '300px', objectFit: 'contain', marginBottom: '25px' }}
              />
            </div>
          </div>
        )
      }
    </div>
  );
};

export default SalesCenterManualFirebaseSettings;
